import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { XLSX } from '../../../utils/xlsxShim';
import i18n from '../../../i18n';

dayjs.extend(customParseFormat);

/* ─────────────────────────────────────────────────────────────────────────
   Import des fiches victimes par fichier Excel.

   Les victimes du wizard ne sont pas encore enregistrées : la lecture se fait
   donc entièrement dans le navigateur et alimente le tableau local, exactement
   comme une saisie manuelle. Aucun appel serveur.

   Une seule colonne est obligatoire - la situation - alignée sur la règle de
   la fiche manuelle : on saisit vite, on complète ensuite.

   Le même modèle sert à l'onglet Victimes du dossier, où les lignes sont
   enregistrées une à une. Les quatre dernières colonnes y ajoutent les PARTIES
   ADVERSES : « Partie adverse = Oui ». Une partie adverse blessée ou décédée est
   aussi une victime ; indemne (situation vide), elle reste une simple partie
   adverse. Les colonnes sont AJOUTÉES en fin de modèle : un fichier rempli avec
   l'ancien modèle se lit toujours.
   ───────────────────────────────────────────────────────────────────────── */

/* Le modèle reste en français, quelle que soit la langue de l'interface : ses
   valeurs (« Blessé », « Oui », « Véhicule »…) sont relues à l'import. */
export const COLONNES_MODELE = [
  'Situation *',
  'Nom',
  'Prénom(s)',
  'Sexe',
  'Date de naissance',
  'Nationalité',
  'Profession',
  "Statut d'activité",
  'Revenu mensuel',
  'Type de dommage',
  'Partie adverse (Oui/Non)',
  'Nature adverse (Véhicule/Piéton)',
  'Immatriculation adverse',
  "Compagnie d'assurance adverse",
];

const LIGNES_EXEMPLE = [
  ['Blessé', 'LAKMON', 'Adjikamba', 'M', '14/03/1988', 'Togolaise', 'Commerçant', 'Actif', 150000, 'Corporel', 'Non'],
  ['Décédé', 'BABOZA', 'Amos', 'M', '02/11/1975', 'Togolaise', '', 'Actif', '', 'Corporel', 'Non'],
  ['Blessé', 'KODJO', 'Ama', 'F', '05/06/1990', 'Togolaise', 'Couturière', 'Actif', 90000, 'Mixte', 'Oui', 'Véhicule', 'TG-4521-BC', 'NSIA ASSURANCES'],
  ['', 'AGBEKO', 'Kossi', '', '', '', '', '', '', 'Matériel', 'Oui', 'Véhicule', 'TG-7788-AA', ''],
];

const OUI = new Set(['oui', 'o', 'yes', 'y', 'x', '1', 'vrai']);
const INDEMNE = new Set(['', 'indemne', 'neutre', 'aucun', 'non blesse']);

/* Normalisation permissive : sans accents, sans casse, sans espaces superflus. */
const norm = (v) => String(v ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .trim().toLowerCase();

/* Libellés officiels FRANÇAIS des énumérations, tels qu'ils s'écrivent dans un
   fichier d'import. Copie volontaire de variables/constants.js : ces tables-là
   rendent désormais la langue de l'écran, et un index construit sur elles au
   chargement ne reconnaîtrait plus « Actif - salarié, indépendant » ni
   « Élève / étudiant » si l'application démarrait en anglais. Un fichier
   d'import est une donnée en français, quelle que soit la langue de l'agent. */
const SITUATIONS_FR = [
  { value: 'BLESSE', label: 'Blessé(e)' },
  { value: 'DECEDE', label: 'Décédé(e)' },
  { value: 'NEUTRE', label: 'Neutre' },
];
const STATUTS_ACTIVITE_FR = [
  { value: 'ACTIF', label: 'Actif - salarié, indépendant' },
  { value: 'SANS_EMPLOI', label: 'Sans emploi' },
  { value: 'ELEVE_ETUDIANT', label: 'Élève / étudiant' },
  { value: 'RETRAITE', label: 'Retraité' },
];
const TYPES_DOMMAGE_FR = [
  { value: 'CORPOREL', label: 'Corporel' },
  { value: 'MATERIEL', label: 'Matériel' },
  { value: 'MIXTE', label: 'Mixte' },
];

/* Table de correspondance libellé saisi → valeur d'énumération. Accepte le
   libellé officiel, la valeur brute, et les abréviations courantes. */
const construireIndex = (liste, alias = {}) => {
  const idx = {};
  liste.forEach(({ value, label }) => {
    idx[norm(value)] = value;
    idx[norm(label)] = value;
  });
  Object.entries(alias).forEach(([cle, valeur]) => { idx[norm(cle)] = valeur; });
  return idx;
};

const IDX_SITUATION = construireIndex(SITUATIONS_FR, {
  blesse: 'BLESSE', 'blesse(e)': 'BLESSE', blessee: 'BLESSE', 'b': 'BLESSE',
  decede: 'DECEDE', 'decede(e)': 'DECEDE', decedee: 'DECEDE', deces: 'DECEDE', 'd': 'DECEDE',
});
const IDX_STATUT = construireIndex(STATUTS_ACTIVITE_FR, {
  actif: 'ACTIF', salarie: 'ACTIF', independant: 'ACTIF',
  'sans emploi': 'SANS_EMPLOI', chomeur: 'SANS_EMPLOI',
  eleve: 'ELEVE_ETUDIANT', etudiant: 'ELEVE_ETUDIANT', 'eleve/etudiant': 'ELEVE_ETUDIANT',
  retraite: 'RETRAITE',
});
const IDX_DOMMAGE = construireIndex(TYPES_DOMMAGE_FR, {
  corporel: 'CORPOREL', materiel: 'MATERIEL', mixte: 'MIXTE',
});

const SEXES = { m: 'M', h: 'M', masculin: 'M', homme: 'M', f: 'F', feminin: 'F', femme: 'F' };

const FORMATS_DATE = ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'DD.MM.YYYY'];

/* Excel renvoie soit une vraie Date (cellule formatée), soit un nombre de série,
   soit du texte selon la façon dont la colonne a été remplie. */
function lireDate(brut) {
  if (brut == null || brut === '') return null;
  if (brut instanceof Date) return dayjs(brut).format('YYYY-MM-DD');
  if (typeof brut === 'number') {
    const p = XLSX.SSF.parse_date_code(brut);
    if (!p) return null;
    return dayjs(`${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`).format('YYYY-MM-DD');
  }
  const d = dayjs(String(brut).trim(), FORMATS_DATE, true);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

function lireNombre(brut) {
  if (brut == null || brut === '') return null;
  const n = Number(String(brut).replace(/[^\d.,-]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Construit et télécharge le modèle vierge, avec deux lignes d'exemple. */
export async function telechargerModeleVictimes() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([COLONNES_MODELE, ...LIGNES_EXEMPLE]);
  ws['!cols'] = [
    { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 8 }, { wch: 18 },
    { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 16 }, { wch: 16 },
    { wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 28 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Victimes');
  await XLSX.writeFile(wb, 'modele-victimes.xlsx');
}

/**
 * Lit un fichier Excel de victimes et de parties adverses.
 * @returns {Promise<{victimes: object[], adversaires: object[], erreurs: {ligne:number, raison:string}[]}>}
 */
export async function lireVictimesExcel(file) {
  const buffer = await file.arrayBuffer();
  const wb = await XLSX.read(buffer);
  const ws = wb.Sheets[wb.SheetNames[0]];
  const lignes = XLSX.utils.sheet_to_json(ws, { header: 1 });

  const victimes = [];
  const adversaires = [];
  const erreurs = [];

  // La première ligne est l'en-tête : on la saute si elle ressemble au modèle.
  const debut = norm(lignes[0]?.[0]).startsWith('situation') ? 1 : 0;

  for (let i = debut; i < lignes.length; i += 1) {
    const l = lignes[i] || [];
    const numeroLigne = i + 1;

    // Ligne entièrement vide : ignorée en silence (Excel en produit souvent).
    if (l.every((c) => c == null || String(c).trim() === '')) continue;

    const situation = IDX_SITUATION[norm(l[0])];

    /* Partie adverse : la situation est facultative - vide ou « indemne », la
       personne n'est pas atteinte et ne devient pas une victime. */
    if (OUI.has(norm(l[10]))) {
      if (!situation && !INDEMNE.has(norm(l[0]))) {
        erreurs.push({ ligne: numeroLigne, raison: i18n.t('declaration:importExcel.erreurs.situationInconnueOuVide', { valeur: l[0] }) });
        continue;
      }
      const dateNaissanceAdverse = lireDate(l[4]);
      if (l[4] && !dateNaissanceAdverse) {
        erreurs.push({ ligne: numeroLigne, raison: i18n.t('declaration:importExcel.erreurs.dateNaissanceIllisible', { valeur: l[4] }) });
        continue;
      }
      const pieton = norm(l[11]).startsWith('pieton');
      const nom = String(l[1] ?? '').trim() || null;
      const prenoms = String(l[2] ?? '').trim() || null;
      const immatriculation = pieton ? null : (String(l[12] ?? '').trim() || null);
      if (!nom && !prenoms && !immatriculation) {
        erreurs.push({ ligne: numeroLigne, raison: i18n.t('declaration:importExcel.erreurs.partieAdverseSansIdentite') });
        continue;
      }
      const blesse = !!situation;
      adversaires.push({
        estAdversaire: true,
        typeAdversaire: pieton ? 'PIETON' : 'ENGINS',
        typeVictime: situation || 'NEUTRE',
        nom,
        prenoms,
        sexe: blesse ? (SEXES[norm(l[3])] || null) : null,
        dateNaissance: dateNaissanceAdverse,
        nationalite: String(l[5] ?? '').trim() || null,
        profession: blesse ? (String(l[6] ?? '').trim() || null) : null,
        statutActivite: blesse ? (IDX_STATUT[norm(l[7])] || null) : null,
        revenuMensuel: blesse ? lireNombre(l[8]) : null,
        typeDommage: pieton ? 'CORPOREL' : (IDX_DOMMAGE[norm(l[9])] || (blesse ? 'CORPOREL' : 'MATERIEL')),
        immatriculation,
        assureurAdverse: pieton ? null : (String(l[13] ?? '').trim() || null),
        _ligne: numeroLigne,
        _importe: true,
      });
      continue;
    }

    if (!situation) {
      erreurs.push({
        ligne: numeroLigne,
        raison: l[0]
          ? i18n.t('declaration:importExcel.erreurs.situationInconnue', { valeur: l[0] })
          : i18n.t('declaration:importExcel.erreurs.situationManquante'),
      });
      continue;
    }

    const dateNaissance = lireDate(l[4]);
    if (l[4] && !dateNaissance) {
      erreurs.push({ ligne: numeroLigne, raison: i18n.t('declaration:importExcel.erreurs.dateNaissanceIllisible', { valeur: l[4] }) });
      continue;
    }

    victimes.push({
      typeVictime: situation,
      nom: String(l[1] ?? '').trim() || null,
      prenoms: String(l[2] ?? '').trim() || null,
      sexe: SEXES[norm(l[3])] || null,
      dateNaissance,
      nationalite: String(l[5] ?? '').trim() || null,
      profession: String(l[6] ?? '').trim() || null,
      statutActivite: IDX_STATUT[norm(l[7])] || null,
      revenuMensuel: lireNombre(l[8]),
      typeDommage: IDX_DOMMAGE[norm(l[9])] || 'CORPOREL',
      _ligne: numeroLigne,
      _importe: true,
    });
  }

  return { victimes, adversaires, erreurs };
}
